import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ClientDashboard } from './pages/client/ClientDashboard'
import { DomainManagement } from './pages/client/DomainManagement'
import { DomainManagePage } from './pages/client/DomainManagePage'
import { DatabaseManagement } from './pages/client/DatabaseManagement'
import { FileManager } from './pages/client/FileManager'
import { EmailManager } from './pages/client/EmailManager'
import { RuntimePicker } from './pages/client/RuntimePicker'
import { Privacy } from './pages/client/Privacy'
import { DnsEditor } from './pages/client/DnsEditor'
import { PhpSettings } from './pages/client/PhpSettings'
import { WordPressManager } from './pages/client/WordPressManager'
import { WordPressStagingPage } from './pages/client/WordPressStagingPage'
import { EmailV2 } from './pages/client/EmailV2'
import { Branding } from './pages/client/Branding'
import { BackupsPage } from './components/Backups/BackupsPage'
import { CreateBackupPage } from './pages/client/CreateBackupPage'
import { RestoreBackupPage } from './pages/client/RestoreBackupPage'
import { SFTPInfo } from './pages/client/SFTPInfo'
import { Container } from './pages/client/Container'
import { Account } from './pages/client/Account'
import { Sites } from './pages/client/Sites'
import { RequestSitePage } from './pages/client/RequestSitePage'
import { ParkDomainPage } from './pages/client/ParkDomainPage'
import { AddSubdomainPage } from './pages/client/AddSubdomainPage'
import { CreateDatabasePage } from './pages/client/CreateDatabasePage'
import { MailboxPasswordPage } from './pages/client/MailboxPasswordPage'
import { ClientShell } from './components/client/ClientShell'
import { AuthProvider } from '@hpanel/web-common/contexts/AuthContext'
import { PluginRoutes } from '@hpanel/web-common/plugins'
import { PasswordReset } from '@hpanel/web-common/pages/PasswordReset'
import { Login } from '@hpanel/web-common/pages/Login'
import { Register } from '@hpanel/web-common/pages/Register'
import { ChangePassword } from '@hpanel/web-common/pages/ChangePassword'
import { ProtectedRoute } from '@hpanel/web-common/components/ProtectedRoute'

// hpanel-client is the tenant User Control Plane SPA. Admin/host routes
// live in apps/hpanel-root. Unknown paths redirect to /client (the
// dashboard) — `client` users have no business at /dashboard or /host/*.
/**
 * Routes live in their own component so that `PluginRoutes()` — which is
 * CALLED, not rendered, because <Routes> only accepts <Route> children — runs
 * its hooks inside the AuthProvider.
 *
 * Calling it from App() instead put those hooks in App's own hook context,
 * which sits OUTSIDE the AuthProvider App renders. Once `usePluginPages` began
 * calling `useAuth` (3b4b0e5), that threw "useAuth must be used within an
 * AuthProvider" and took down every page of the SPA on load. It went unnoticed
 * because the deployed panel was serving a `dist/` built before that commit.
 * Regression test: tests/ui/qa/unit/spa-boots.spec.ts.
 */
function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/client" replace />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/login" element={<Login showRegisterLink />} />
        <Route path="/register" element={<Register />} />
        {/* Outside the shells (no sidebar); the only protected page an
            account that must change its password can see. */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute allowPendingPasswordChange>
              <ChangePassword home="/client" />
            </ProtectedRoute>
          }
        />

        {/* Every tenant screen: authentication and chrome come from
            ClientShell, not from each page wrapping itself. EmailV2 had
            already drifted out of its ClientLayout wrapper — a layout route
            makes that class of miss impossible. */}
        <Route element={<ClientShell />}>
          <Route path="/client" element={<ClientDashboard />} />
          <Route path="/client/domains" element={<DomainManagement />} />
          {/* The add-domain wizard is retired: a site is requested, an administrator approves it. */}
          <Route path="/client/domains/new" element={<Navigate to="/client/sites/request" replace />} />
          <Route path="/client/sites" element={<Sites />} />
          <Route path="/client/sites/request" element={<RequestSitePage />} />
          <Route path="/client/sites/:containerId/park" element={<ParkDomainPage />} />
          <Route path="/client/sites/:containerId/subdomains/new" element={<AddSubdomainPage />} />
          <Route path="/client/domains/:id" element={<DomainManagePage />} />
          <Route path="/client/databases" element={<DatabaseManagement />} />
          <Route path="/client/databases/new" element={<CreateDatabasePage />} />
          <Route path="/client/files" element={<FileManager />} />
          <Route path="/client/email" element={<EmailManager />} />
          <Route path="/client/email/mailboxes/:id/password" element={<MailboxPasswordPage />} />
          <Route path="/client/runtime" element={<RuntimePicker />} />
          <Route path="/client/account" element={<Account />} />
          <Route path="/client/account/privacy" element={<Privacy />} />
          <Route path="/client/dns" element={<DnsEditor />} />
          <Route path="/client/php" element={<PhpSettings />} />
          <Route path="/client/wordpress" element={<WordPressManager />} />
          <Route path="/client/wordpress/:id/staging" element={<WordPressStagingPage />} />
          <Route path="/client/email-v2" element={<EmailV2 />} />
          <Route path="/client/account/branding" element={<Branding />} />
          <Route path="/client/backups" element={<BackupsPage />} />
          <Route path="/client/backups/new" element={<CreateBackupPage />} />
          <Route path="/client/backups/:id/restore" element={<RestoreBackupPage />} />
          <Route path="/client/sftp" element={<SFTPInfo />} />
          <Route path="/client/container" element={<Container />} />

          {/* Plugin-contributed routes — inside the shell so plugin pages
              keep the panel chrome (bug #3). PluginRoutes owns the catch-all
              so an async-loading plugin route isn't beaten to the match by
              `*` on a cold deep-link. */}
          {PluginRoutes({ fallback: <Navigate to="/client" replace /> })}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}

export default App
